/* eslint-disable complexity */
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { getStorageConfig } from 'reactApp/modules/storage/storage.config';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { EFileError, EFileStatus } from 'reactApp/modules/uploadList/uploadList.model';
import { updateUploadFilesAction } from 'reactApp/modules/uploadList/uploadList.module';
import { DataListItemThumbProps } from 'reactApp/ui/DataListItemThumb/DataListItemThumb.types';

import { useItemAuthor } from './useItemAuthors';

// eslint-disable-next-line max-lines-per-function
export const useThumbProps = ({
    item,
    uploadingItem,
    isSelected,
    isActive,
    isSelecting,
    handleOnCheckboxClick: onCheckboxClick,
    showCheckbox,
    thumb,
    tipData,
    id,
    onDownload,
    view,
    onFavorites,
    onPublish,
    favoritesAvailable,
    isVirus,
    isPublicDownloaded,
    isPopup,
}): DataListItemThumbProps | undefined => {
    const dispatch = useDispatch();
    const author = useItemAuthor(item);
    const storageConfig = getStorageConfig(item.storage);
    const isFavoritesAvailable = storageConfig.favorites;

    const onCancelUpload = useCallback(() => {
        if (!uploadingItem.descriptorId && !uploadingItem.cloudPath) {
            return;
        }

        dispatch(
            updateUploadFilesAction({
                cloudPath: uploadingItem.cloudPath,
                descriptorId: uploadingItem.descriptorId,
                status: EFileStatus.CANCEL,
                error: EFileError.CANCELLED_FILE,
                hideError: true,
            })
        );
    }, [dispatch, uploadingItem]);

    if (view !== 'thumbs' || !item) {
        return;
    }

    const basicThumbProps = {
        isSelected,
        isActive,
        isSelecting,
        onCheckboxClick,
        name: item.isFolder ? item.name : item.nameWithoutExt,
        forceCheckbox: Boolean(showCheckbox),
        onDownload,
        kind: item.storage === EStorageType.attaches ? 'file' : item.kind,
        thumbUrl: thumb,
        tipData,
        id,
        isPublicDownloaded,
        isPopup,
    };

    // eslint-disable-next-line sonarjs/max-switch-cases
    switch (item.storage) {
        case EStorageType.home: {
            return {
                ...basicThumbProps,
                size: item.size,
                ext: item.isFolder ? undefined : item.ext,
                isInFavorites: item.isInFavorites,
                isPublic: !!item.weblink,
                isAutoDelete: item?.weblinkAutoDelete,
                publicExpires: item?.weblinkExpires,
                isFolder: item.isFolder,
                onFavorites,
                onPublish,
                favoritesAvailable,
                isVirus,
                storage: item.storage,
                onCancelUpload,
            };
        }
        case EStorageType.sharedLinks:
        case EStorageType.sharedAutodelete: {
            return {
                ...basicThumbProps,
                size: item.size,
                ext: item.isFolder ? undefined : item.ext,
                isPublic: !!item.weblink,
                isAutoDelete: item?.weblinkAutoDelete,
                publicExpires: item?.weblinkExpires,
                isFolder: item.isFolder,
                onPublish,
                isVirus,
                storage: item.storage,
            };
        }
        case EStorageType.public: {
            return {
                ...basicThumbProps,
                size: item.size,
                ext: item.isFolder ? undefined : item.ext,
                isFolder: item.isFolder,
                isVirus,
                storage: item.storage,
                owner: author,
            };
        }
        case EStorageType.favorites: {
            return {
                ...basicThumbProps,
                size: item.size,
                ext: item.ext,
                isInFavorites: true,
                isPublic: !!item.weblink,
                isAutoDelete: item?.weblinkAutoDelete,
                publicExpires: item?.weblinkExpires,
                favoritesAvailable: true,
                isFolder: item.isFolder,
                onFavorites,
                onPublish,
                isVirus,
                storage: item.storage,
            };
        }
        case EStorageType.stock: {
            return {
                ...basicThumbProps,
                size: item.isFolder ? undefined : item.size,
                ext: item.isFolder ? undefined : item.ext,
                isFolder: item.isFolder,
                isVirus,
                storage: item.storage,
            };
        }
        case EStorageType.search: {
            return {
                ...basicThumbProps,
                size: item.isFolder ? undefined : item.size,
                ext: item.isFolder ? undefined : item.ext,
                isFolder: item.isFolder,
                isVirus,
                storage: item.storage,
                isPublic: !!item.weblink,
                isAutoDelete: item?.weblinkAutoDelete,
                publicExpires: item?.weblinkExpires,
                onPublish,
                isInFavorites: item.isInFavorites,
                favoritesAvailable,
                onFavorites,
            };
        }
        case EStorageType.attaches: {
            return {
                ...basicThumbProps,
                size: item.size,
                ext: item.ext,
                isFolder: false,
                storage: item.storage,
                owner: {
                    name: item.authorName,
                    email: item.authorEmail,
                },
            };
        }
        case EStorageType.feed: {
            return {
                ...basicThumbProps,
                size: item.size,
                ext: item.ext,
                isFolder: false,
                isVirus,
                storage: item.storage,
                isPublic: !!item.weblink,
                onPublish,
            };
        }
        case EStorageType.gallery:
        case EStorageType.documents: {
            return {
                ...basicThumbProps,
                size: item.size,
                ext: item.ext,
                isFolder: false,
                isVirus,
                favoritesAvailable: !item.isMounted,
                storage: item.storage,
                isPublic: Boolean(item.weblink),
                isInFavorites: item.isInFavorites,
                onPublish,
                onFavorites,
            };
        }
        case EStorageType.albums: {
            return {
                ...basicThumbProps,
                size: item.size,
                ext: item.ext,
                isFolder: false,
                isVirus,
                storage: item.storage,
                isInFavorites: item.isInFavorites,
                onFavorites,
                favoritesAvailable: isFavoritesAvailable,
            };
        }
        case EStorageType.quotaCleaner: {
            return {
                ...basicThumbProps,
                size: item.size,
                ext: item.ext,
                isFolder: item.isFolder,
                isVirus,
                storage: item.storage,
                favoritesAvailable: false,
            };
        }
    }
};
